import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import AnimalTypeSelector from "@/components/AnimalTypeSelector";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";

export default function EditCaseDialog({ open, onOpenChange, caseItem, onUpdated }) {
  const [animalName, setAnimalName] = useState(caseItem.animal_name);
  const [animalType, setAnimalType] = useState(caseItem.animal_type);
  const [speciesOther, setSpeciesOther] = useState(caseItem.species_other || "");
  const [breed, setBreed] = useState(caseItem.breed || "");
  const [color, setColor] = useState(caseItem.color || "");
  const [admissionDate, setAdmissionDate] = useState(caseItem.admission_date || "");
  const [caseHistory, setCaseHistory] = useState(caseItem.case_history || "");
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/cases/${caseItem.id}`, {
        animal_name: animalName.trim(),
        animal_type: animalType,
        species_other: animalType === "Other" ? speciesOther : "",
        breed,
        color,
        admission_date: admissionDate || null,
        case_history: caseHistory,
      });
      toast({ title: "Case details updated" });
      onUpdated();
      onOpenChange(false);
    } catch (err) {
      toast({ variant: "destructive", title: "Could not update case", description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-case-dialog" className="rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Edit Case</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <Label htmlFor="edit-animal-name">Animal Name</Label>
            <Input id="edit-animal-name" value={animalName} onChange={(e) => setAnimalName(e.target.value)} required data-testid="edit-case-name-input" className="mt-1.5 h-11" />
          </div>

          <div>
            <Label className="mb-2 block">Animal Type</Label>
            <AnimalTypeSelector
              value={animalType}
              onChange={setAnimalType}
              speciesOther={speciesOther}
              onSpeciesOtherChange={setSpeciesOther}
              testIdPrefix="edit-case-animal-type"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="edit-breed">Breed</Label>
              <Input id="edit-breed" value={breed} onChange={(e) => setBreed(e.target.value)} data-testid="edit-case-breed-input" className="mt-1.5 h-11" />
            </div>
            <div>
              <Label htmlFor="edit-color">Color</Label>
              <Input id="edit-color" value={color} onChange={(e) => setColor(e.target.value)} data-testid="edit-case-color-input" className="mt-1.5 h-11" />
            </div>
          </div>

          <div>
            <Label htmlFor="edit-admission-date">Date of Admission</Label>
            <Input id="edit-admission-date" type="date" value={admissionDate} onChange={(e) => setAdmissionDate(e.target.value)} data-testid="edit-case-admission-date-input" className="mt-1.5 h-11" />
          </div>

          <div>
            <Label htmlFor="edit-case-history">Case History / Condition</Label>
            <Textarea id="edit-case-history" value={caseHistory} onChange={(e) => setCaseHistory(e.target.value)} data-testid="edit-case-history-input" className="mt-1.5 min-h-[80px]" />
          </div>

          <DialogFooter>
            <Button type="submit" disabled={saving} className="w-full h-11 bg-amtm-teal text-white hover:bg-amtm-tealHover" data-testid="save-edit-case-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
